import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart3,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Columns3,
  Download,
  Eye,
  KeyRound,
  Link2,
  MoreHorizontal,
  PencilLine,
  Plus,
  Search,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  UserRound,
} from 'lucide-react';
import AddUserModal from './AddUserModal';
import ViewUserModal from './ViewUserModal';
import ChangePasswordModal from './ChangePasswordModal';
import ConfirmDialog from '../../Dialogs/ConfirmDialog';
import ViewRoutePrivilegesModal, { type RoutePrivilege } from '../Access/ViewRoutePrivilegesModal';
import { mergeRoleAndUserPrivileges } from '../Access/mergeRoutePrivileges';
import { mockRoutes } from '../Access/routePrivilegeTemplates';
import { apiService } from '../../../../components/api/apiService';
import { authservice } from '../../../../components/api/authservice';
import { isExternalUser } from '../../interfaces/Users';
import { useAccessTheme } from './useAccessTheme';
import EntraIcon from './identityProviders/EntraIcon';
import { logAccess } from './accessSettingsLogger';
import {
  ACCESS_TENANT_ID,
  PAGE_SIZE,
  displayName,
  formatRelativeTime,
  isWorkspaceAdmin,
  mapDirectoryUser,
  sourceLabel,
  statusLabel,
  userInitials,
  userSourceKey,
  userStatus,
  visiblePageNumbers,
  type DirectorySourceKey,
  type DirectoryStatus,
  type DirectoryUser,
} from './accessDirectoryUtils';

type ColumnKey = 'role' | 'source' | 'status' | 'lastActive';

interface UsersDirectoryPanelProps {
  openAddUser: boolean;
  onOpenAddUserChange: (open: boolean) => void;
  focusAdmins?: boolean;
  onClearAdminFocus?: () => void;
}

const SIDEBAR_STATUSES: DirectoryStatus[] = ['active', 'inactive', 'invited'];
const MORE_STATUSES: DirectoryStatus[] = ['suspended', 'none'];
const SIDEBAR_SOURCES: DirectorySourceKey[] = ['manual', 'entra', 'google'];
const MORE_SOURCES: DirectorySourceKey[] = ['okta', 'scim', 'saml', 'oidc', 'external'];

function toggleValue<T extends string>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

const UsersDirectoryPanel: React.FC<UsersDirectoryPanelProps> = ({
  openAddUser,
  onOpenAddUserChange,
  focusAdmins = false,
  onClearAdminFocus,
}) => {
  const t = useAccessTheme();
  const [search, setSearch] = useState('');
  const [sidebarQuery, setSidebarQuery] = useState('');
  const [roleFilters, setRoleFilters] = useState<string[]>([]);
  const [statusFilters, setStatusFilters] = useState<DirectoryStatus[]>([]);
  const [sourceFilters, setSourceFilters] = useState<DirectorySourceKey[]>([]);
  const [users, setUsers] = useState<DirectoryUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>({
    role: true,
    source: true,
    status: true,
    lastActive: true,
  });
  const [selectedUserForPassword, setSelectedUserForPassword] = useState<DirectoryUser | null>(null);
  const [userToDelete, setUserToDelete] = useState<DirectoryUser | null>(null);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [viewUserId, setViewUserId] = useState<string | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [routesUser, setRoutesUser] = useState<DirectoryUser | null>(null);
  const [routesForUser, setRoutesForUser] = useState<RoutePrivilege[]>([]);
  const [firstPageIdForUser, setFirstPageIdForUser] = useState<string | null>(null);
  const [showRoutesModal, setShowRoutesModal] = useState(false);
  const [openMenuUserId, setOpenMenuUserId] = useState<string | null>(null);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [openSections, setOpenSections] = useState({ role: true, status: true, source: true });
  const columnsRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);

  const refreshUserList = async () => {
    setLoading(true);
    try {
      const res = await authservice.getUsers();
      const mapped = (Array.isArray(res) ? res : []).map((row) => mapDirectoryUser(row as DirectoryUser));
      setUsers(mapped);
      logAccess('app', 'users_loaded', { count: mapped.length });
    } catch (err) {
      logAccess('error', 'users_load_failed', {
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refreshUserList();
  }, []);

  useEffect(() => {
    if (!openAddUser) return;
    setEditUserId(null);
    setShowUserModal(true);
  }, [openAddUser]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (columnsRef.current && !columnsRef.current.contains(target)) setColumnsOpen(false);
      if (moreRef.current && !moreRef.current.contains(target)) setMoreOpen(false);
      if (menuRef.current && !menuRef.current.contains(target)) setOpenMenuUserId(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const roleOptions = useMemo(() => {
    const names = Array.from(
      new Set(users.map((user) => (user.roleName || user.roleID || '').trim()).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));
    return names;
  }, [users]);

  const visibleRoleOptions = useMemo(() => {
    const query = sidebarQuery.trim().toLowerCase();
    if (!query) return roleOptions;
    return roleOptions.filter((role) => role.toLowerCase().includes(query));
  }, [roleOptions, sidebarQuery]);

  const roleCounts = useMemo(() => {
    const map = new Map<string, number>();
    users.forEach((user) => {
      const role = (user.roleName || user.roleID || '').trim();
      if (!role) return;
      map.set(role, (map.get(role) || 0) + 1);
    });
    return map;
  }, [users]);

  const statusCounts = useMemo(() => {
    const map = new Map<DirectoryStatus, number>();
    users.forEach((user) => {
      const status = userStatus(user);
      map.set(status, (map.get(status) || 0) + 1);
    });
    return map;
  }, [users]);

  const sourceCounts = useMemo(() => {
    const map = new Map<DirectorySourceKey, number>();
    users.forEach((user) => {
      const source = userSourceKey(user);
      map.set(source, (map.get(source) || 0) + 1);
    });
    return map;
  }, [users]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return users.filter((user) => {
      if (focusAdmins && !isWorkspaceAdmin(user)) return false;
      const role = user.roleName || user.roleID || '';
      if (roleFilters.length > 0 && !roleFilters.includes(role)) return false;
      if (statusFilters.length > 0 && !statusFilters.includes(userStatus(user))) return false;
      if (sourceFilters.length > 0 && !sourceFilters.includes(userSourceKey(user))) return false;
      if (!query) return true;
      return `${displayName(user)} ${user.email}`.toLowerCase().includes(query);
    });
  }, [users, search, roleFilters, statusFilters, sourceFilters, focusAdmins]);

  const pageCount = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [search, roleFilters, statusFilters, sourceFilters, focusAdmins]);

  useEffect(() => {
    setPage((current) => Math.min(current, pageCount));
  }, [pageCount]);

  const pagedUsers = filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const rangeStart = filteredUsers.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, filteredUsers.length);
  const filtersActive =
    search.trim() !== '' ||
    sidebarQuery.trim() !== '' ||
    roleFilters.length > 0 ||
    statusFilters.length > 0 ||
    sourceFilters.length > 0 ||
    focusAdmins;
  const visibleCount = 2 + Object.values(visibleColumns).filter(Boolean).length;

  const closeUserModal = () => {
    setShowUserModal(false);
    setEditUserId(null);
    onOpenAddUserChange(false);
  };

  const handleDeleteUser = async (target: DirectoryUser) => {
    try {
      await authservice.deleteUser(target.id, target.tenantId || ACCESS_TENANT_ID);
      setUsers((prev) => prev.filter((user) => user.id !== target.id));
      logAccess('app', 'user_deleted', { userId: target.id, email: target.email });
    } catch (err) {
      logAccess('error', 'user_delete_failed', {
        userId: target.id,
        message: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const handleViewUserRoutes = async (user: DirectoryUser) => {
    const template = mockRoutes;
    try {
      const [roleRoutes, userRoutes] = await Promise.all([
        user.roleID
          ? apiService.get('/api/RoutePermission', { tenantId: ACCESS_TENANT_ID, roleId: user.roleID }, 'backend')
          : Promise.resolve([]),
        apiService.get('/api/RoutePermission/user', { userId: user.id }, 'backend'),
      ]);
      const rolePayload = Array.isArray(roleRoutes) ? roleRoutes : roleRoutes?.routes;
      const userPayload = Array.isArray(userRoutes) ? userRoutes : userRoutes?.routes;
      setRoutesForUser(
        mergeRoleAndUserPrivileges(
          template as RoutePrivilege[],
          Array.isArray(rolePayload) ? rolePayload : [],
          Array.isArray(userPayload) ? userPayload : []
        )
      );
      setFirstPageIdForUser(userRoutes?.firstPageId || roleRoutes?.firstPageId || null);
      setRoutesUser(user);
      setShowRoutesModal(true);
      logAccess('app', 'user_routes_opened', { userId: user.id });
    } catch (error) {
      logAccess('error', 'user_routes_load_failed', {
        userId: user.id,
        message: error instanceof Error ? error.message : String(error),
      });
      setRoutesForUser(mergeRoleAndUserPrivileges(template as RoutePrivilege[], [], []));
      setFirstPageIdForUser(null);
      setRoutesUser(user);
      setShowRoutesModal(true);
    }
  };

  const handlePasswordUpdate = async (newPassword: string) => {
    if (!selectedUserForPassword) return;
    try {
      await authservice.updatePassword(
        selectedUserForPassword.id,
        newPassword,
        selectedUserForPassword.tenantId || ACCESS_TENANT_ID
      );
      logAccess('app', 'user_password_updated', { userId: selectedUserForPassword.id });
    } catch (err) {
      logAccess('error', 'user_password_update_failed', {
        userId: selectedUserForPassword.id,
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setSelectedUserForPassword(null);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setSidebarQuery('');
    setRoleFilters([]);
    setStatusFilters([]);
    setSourceFilters([]);
    onClearAdminFocus?.();
  };

  const exportCsv = () => {
    const headers = ['Name', 'Email', 'Role', 'Source', 'Status', 'Last active'];
    const rows = filteredUsers.map((user) => [
      displayName(user),
      user.email,
      user.roleName || user.roleID || '',
      sourceLabel(userSourceKey(user)),
      statusLabel(userStatus(user)),
      formatRelativeTime(user.lastLoginAt),
    ]);
    const csv = [headers, ...rows]
      .map((cols) => cols.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'access-users.csv';
    link.click();
    URL.revokeObjectURL(url);
    logAccess('app', 'users_exported', { count: filteredUsers.length });
  };

  const filterSelectClass = `appearance-none border rounded-lg pl-8 pr-8 h-9 text-sm outline-none min-w-[140px] ${t.input}`;

  const statusChip = (status: DirectoryStatus) => {
    if (status === 'active') {
      return t.isLight
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
        : 'bg-emerald-900/30 text-emerald-300 border-emerald-800';
    }
    if (status === 'invited') {
      return t.isLight
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-amber-900/30 text-amber-300 border-amber-800';
    }
    if (status === 'suspended') {
      return t.isLight
        ? 'bg-rose-50 text-rose-700 border-rose-200'
        : 'bg-rose-900/30 text-rose-300 border-rose-800';
    }
    return t.badge;
  };

  const sourceIcon = (key: DirectorySourceKey) => {
    if (key === 'entra') return <EntraIcon className="w-4 h-4" />;
    if (key === 'okta') return <span className="w-4 h-4 rounded-full border-2 border-indigo-400 inline-block" />;
    if (key === 'scim') return <Settings2 className="w-4 h-4" />;
    return <UserRound className={`w-4 h-4 ${t.muted}`} />;
  };

  const roleChip = (user: DirectoryUser) => {
    const name = user.roleName || user.roleID || '—';
    const admin = user.isadmin || name.toLowerCase() === 'admin';
    if (admin) {
      return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${t.isLight ? 'bg-violet-50 text-violet-700' : t.badgeAccent}`}>
          <ShieldCheck className="w-3.5 h-3.5" /> {name}
        </span>
      );
    }
    if (name.toLowerCase().includes('analytics')) {
      return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${t.badge}`}>
          <BarChart3 className="w-3.5 h-3.5" /> {name}
        </span>
      );
    }
    return (
      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${t.badge}`}>{name}</span>
    );
  };

  const sectionToggle = (key: keyof typeof openSections) => (
    <button
      type="button"
      onClick={() => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }))}
      className={`w-full flex items-center justify-between text-sm font-medium py-1 ${t.title}`}
    >
      {key === 'role' ? 'Role' : key === 'status' ? 'Status' : 'Source'}
      <ChevronDown className={`w-4 h-4 transition-transform ${openSections[key] ? '' : '-rotate-90'} ${t.muted}`} />
    </button>
  );

  const checkboxRow = (label: string, count: number, checked: boolean, onChange: () => void) => (
    <label className={`flex items-center gap-2 py-1 text-sm cursor-pointer ${t.body}`}>
      <input type="checkbox" checked={checked} onChange={onChange} className="rounded border-neutral-300" />
      <span className="flex-1 truncate">{label}</span>
      <span className={t.muted}>{count}</span>
    </label>
  );

  return (
    <div className="flex flex-col lg:flex-row gap-4 items-start">
      <aside className={`w-full lg:w-56 shrink-0 rounded-2xl border p-4 ${t.card}`}>
        <div className="flex items-center justify-between mb-3">
          <span className={`text-sm font-semibold ${t.title}`}>Filters</span>
          <button
            type="button"
            onClick={clearFilters}
            disabled={!filtersActive}
            className={`text-sm ${t.accentText} disabled:opacity-40`}
          >
            Reset
          </button>
        </div>
        <div className="relative mb-4">
          <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${t.muted}`} />
          <input
            type="text"
            placeholder="Search users..."
            value={sidebarQuery}
            onChange={(e) => setSidebarQuery(e.target.value)}
            className={`w-full border rounded-lg pl-8 pr-2 h-8 text-sm outline-none ${t.input}`}
          />
        </div>
        <div className="space-y-3">
          <div>
            {sectionToggle('role')}
            {openSections.role ? (
              <div className="mt-1 space-y-0.5">
                {visibleRoleOptions.map((role) =>
                  checkboxRow(role, roleCounts.get(role) || 0, roleFilters.includes(role), () =>
                    setRoleFilters((prev) => toggleValue(prev, role))
                  )
                )}
                {visibleRoleOptions.length === 0 ? <p className={`text-xs ${t.muted}`}>No roles</p> : null}
              </div>
            ) : null}
          </div>
          <div>
            {sectionToggle('status')}
            {openSections.status ? (
              <div className="mt-1 space-y-0.5">
                {SIDEBAR_STATUSES.map((status) =>
                  checkboxRow(statusLabel(status), statusCounts.get(status) || 0, statusFilters.includes(status), () =>
                    setStatusFilters((prev) => toggleValue(prev, status))
                  )
                )}
              </div>
            ) : null}
          </div>
          <div>
            {sectionToggle('source')}
            {openSections.source ? (
              <div className="mt-1 space-y-0.5">
                {SIDEBAR_SOURCES.map((source) =>
                  checkboxRow(sourceLabel(source), sourceCounts.get(source) || 0, sourceFilters.includes(source), () =>
                    setSourceFilters((prev) => toggleValue(prev, source))
                  )
                )}
              </div>
            ) : null}
          </div>
        </div>
      </aside>

      <div className={`flex-1 min-w-0 rounded-2xl border ${t.card}`}>
        <div className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${t.muted}`} />
              <input
                type="text"
                placeholder="Search by name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`w-full border rounded-lg pl-9 pr-3 h-9 text-sm outline-none ${t.input}`}
              />
            </div>
            <button
              type="button"
              onClick={() => onOpenAddUserChange(true)}
              className={`inline-flex items-center gap-2 px-4 h-9 text-sm font-medium rounded-lg shrink-0 shadow-sm ${t.btnPrimary}`}
            >
              <Plus className="w-4 h-4" /> Add user
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <UserRound className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${t.muted}`} />
              <select
                value={roleFilters.length === 1 ? roleFilters[0] : 'all'}
                onChange={(e) => setRoleFilters(e.target.value === 'all' ? [] : [e.target.value])}
                className={filterSelectClass}
              >
                <option value="all">All roles</option>
                {roleOptions.map((role) => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
              <ChevronDown className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 ${t.muted}`} />
            </div>
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-emerald-500" />
              <select
                value={statusFilters.length === 1 ? statusFilters[0] : 'all'}
                onChange={(e) => setStatusFilters(e.target.value === 'all' ? [] : [e.target.value as DirectoryStatus])}
                className={filterSelectClass}
              >
                <option value="all">All statuses</option>
                {[...SIDEBAR_STATUSES, ...MORE_STATUSES].map((status) => (
                  <option key={status} value={status}>{statusLabel(status)}</option>
                ))}
              </select>
              <ChevronDown className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 ${t.muted}`} />
            </div>
            <div className="relative">
              <Link2 className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${t.muted}`} />
              <select
                value={sourceFilters.length === 1 ? sourceFilters[0] : 'all'}
                onChange={(e) => setSourceFilters(e.target.value === 'all' ? [] : [e.target.value as DirectorySourceKey])}
                className={filterSelectClass}
              >
                <option value="all">All sources</option>
                {[...SIDEBAR_SOURCES, ...MORE_SOURCES].map((source) => (
                  <option key={source} value={source}>{sourceLabel(source)}</option>
                ))}
              </select>
              <ChevronDown className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 ${t.muted}`} />
            </div>
            <div className="relative" ref={moreRef}>
              <button
                type="button"
                onClick={() => setMoreOpen((open) => !open)}
                className={`inline-flex items-center gap-1.5 border rounded-lg px-3 h-9 text-sm ${t.btnQuiet}`}
              >
                <SlidersHorizontal className="w-3.5 h-3.5" /> More filters
              </button>
              {moreOpen ? (
                <div className={`absolute z-20 mt-1 w-56 rounded-xl border shadow-lg p-3 ${t.panel}`}>
                  <p className={`text-xs font-medium mb-2 ${t.muted}`}>Status</p>
                  {MORE_STATUSES.map((status) =>
                    checkboxRow(statusLabel(status), statusCounts.get(status) || 0, statusFilters.includes(status), () =>
                      setStatusFilters((prev) => toggleValue(prev, status))
                    )
                  )}
                  <p className={`text-xs font-medium mt-3 mb-2 ${t.muted}`}>Source</p>
                  {MORE_SOURCES.map((source) =>
                    checkboxRow(sourceLabel(source), sourceCounts.get(source) || 0, sourceFilters.includes(source), () =>
                      setSourceFilters((prev) => toggleValue(prev, source))
                    )
                  )}
                </div>
              ) : null}
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={exportCsv}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 h-9 text-sm ${t.btnQuiet}`}
              >
                <Download className="w-4 h-4" /> Export
              </button>
              <div className="relative" ref={columnsRef}>
                <button
                  type="button"
                  onClick={() => setColumnsOpen((open) => !open)}
                  className={`inline-flex items-center justify-center rounded-lg w-9 h-9 ${t.btnQuiet}`}
                  title="Columns"
                >
                  <Columns3 className="w-4 h-4" />
                </button>
                {columnsOpen ? (
                  <div className={`absolute right-0 z-20 mt-1 w-48 rounded-lg border shadow-lg p-2 ${t.panel}`}>
                    {([
                      ['role', 'Role'],
                      ['source', 'Source'],
                      ['status', 'Status'],
                      ['lastActive', 'Last active'],
                    ] as [ColumnKey, string][]).map(([key, label]) => (
                      <label key={key} className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded-md ${t.rowHover}`}>
                        <input
                          type="checkbox"
                          checked={visibleColumns[key]}
                          onChange={() => setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }))}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className={`${t.directoryHead} text-xs`}>
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Email</th>
                {visibleColumns.role ? <th className="px-4 py-3 font-medium">Role</th> : null}
                {visibleColumns.source ? <th className="px-4 py-3 font-medium">Source</th> : null}
                {visibleColumns.status ? <th className="px-4 py-3 font-medium">Status</th> : null}
                {visibleColumns.lastActive ? <th className="px-4 py-3 font-medium">Last active</th> : null}
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={visibleCount + 1} className={`px-4 py-12 text-center ${t.muted}`}>Loading users...</td>
                </tr>
              ) : pagedUsers.length > 0 ? (
                pagedUsers.map((user) => {
                  const status = userStatus(user);
                  const source = userSourceKey(user);
                  return (
                    <tr key={user.id} className={`border-t last:border-b-0 ${t.rowBorder} ${t.rowHover}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0 !text-black"
                            style={{ backgroundColor: 'rgb(223, 223, 224)' }}
                          >
                            {userInitials(user)}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className={`font-normal ${t.title}`}>{displayName(user)}</span>
                              {isExternalUser(user) ? (
                                <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] ${t.badge}`}>Synced</span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className={`px-4 py-3 ${t.muted}`}>{user.email}</td>
                      {visibleColumns.role ? <td className="px-4 py-3">{roleChip(user)}</td> : null}
                      {visibleColumns.source ? (
                        <td className="px-4 py-3">
                          <div className={`inline-flex items-center gap-2 ${t.body}`}>
                            {sourceIcon(source)}
                            {sourceLabel(source)}
                          </div>
                        </td>
                      ) : null}
                      {visibleColumns.status ? (
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs border ${statusChip(status)}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${status === 'invited' ? 'bg-amber-500' : status === 'active' ? 'bg-emerald-500' : 'bg-current'}`} />
                            {statusLabel(status)}
                          </span>
                        </td>
                      ) : null}
                      {visibleColumns.lastActive ? (
                        <td className={`px-4 py-3 ${t.body}`}>{formatRelativeTime(user.lastLoginAt)}</td>
                      ) : null}
                      <td className="px-4 py-3 text-right">
                        <div className="relative inline-block" ref={openMenuUserId === user.id ? menuRef : undefined}>
                          <button
                            type="button"
                            onClick={() => setOpenMenuUserId((current) => (current === user.id ? null : user.id))}
                            className={`p-1.5 rounded-lg ${t.muted} ${t.actionHover}`}
                            title="Actions"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                          {openMenuUserId === user.id ? (
                            <div className={`absolute right-0 z-20 mt-1 w-48 rounded-lg border shadow-lg p-1 ${t.panel}`}>
                              <button type="button" onClick={() => { setOpenMenuUserId(null); setViewUserId(user.id); }}
                                className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm ${t.rowHover}`}>
                                <Eye className="w-4 h-4" /> View
                              </button>
                              <button type="button" onClick={() => { setOpenMenuUserId(null); setEditUserId(user.id); setShowUserModal(true); }}
                                className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm ${t.rowHover}`}>
                                <PencilLine className="w-4 h-4" /> Edit
                              </button>
                              <button type="button" onClick={() => { setOpenMenuUserId(null); void handleViewUserRoutes(user); }}
                                className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm ${t.rowHover}`}>
                                <ShieldCheck className="w-4 h-4" /> Access Setting
                              </button>
                              <button type="button" onClick={() => { setOpenMenuUserId(null); setSelectedUserForPassword(user); }}
                                className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm ${t.rowHover}`}>
                                <KeyRound className="w-4 h-4" /> Change Password
                              </button>
                              {isExternalUser(user) ? (
                                <button type="button" onClick={() => { setOpenMenuUserId(null); setUserToDelete(user); }}
                                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm ${t.rowHover} text-red-500`}>
                                  <Trash2 className="w-4 h-4" /> Delete
                                </button>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={visibleCount + 1} className={`text-center py-12 ${t.muted}`}>No users found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-t ${t.footer}`}>
          <span className={`text-sm ${t.muted}`}>
            Showing {rangeStart}–{rangeEnd} of {filteredUsers.length} users
          </span>
          <div className="flex items-center gap-1">
            <button type="button" disabled={page <= 1} onClick={() => setPage(1)} className={`p-1.5 rounded-md disabled:opacity-30 ${t.actionHover}`}>
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button type="button" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))} className={`p-1.5 rounded-md disabled:opacity-30 ${t.actionHover}`}>
              <ChevronLeft className="w-4 h-4" />
            </button>
            {visiblePageNumbers(page, pageCount).map((number) => (
              <button
                key={number}
                type="button"
                onClick={() => setPage(number)}
                className={`min-w-8 h-8 px-2 rounded-md text-sm transition-colors ${number === page ? `${t.btnPrimary} shadow-sm` : t.btnGhost}`}
              >
                {number}
              </button>
            ))}
            <button type="button" disabled={page >= pageCount} onClick={() => setPage((current) => Math.min(pageCount, current + 1))} className={`p-1.5 rounded-md disabled:opacity-30 ${t.actionHover}`}>
              <ChevronRight className="w-4 h-4" />
            </button>
            <button type="button" disabled={page >= pageCount} onClick={() => setPage(pageCount)} className={`p-1.5 rounded-md disabled:opacity-30 ${t.actionHover}`}>
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {showRoutesModal && routesUser ? (
        <ViewRoutePrivilegesModal
          roleName={`${routesUser.firstName} ${routesUser.lastName}`.trim() || routesUser.email}
          userId={routesUser.id}
          tenantId={ACCESS_TENANT_ID}
          routes={routesForUser}
          firstPageId={firstPageIdForUser}
          onClose={() => {
            setShowRoutesModal(false);
            setRoutesUser(null);
          }}
        />
      ) : null}
      {viewUserId ? <ViewUserModal userId={viewUserId} onClose={() => setViewUserId(null)} /> : null}
      {showUserModal ? (
        <AddUserModal
          key={editUserId || 'new'}
          onClose={closeUserModal}
          onSubmit={() => {
            closeUserModal();
            void refreshUserList();
          }}
          userId={editUserId || undefined}
        />
      ) : null}
      {selectedUserForPassword ? (
        <ChangePasswordModal
          email={selectedUserForPassword.email}
          onClose={() => setSelectedUserForPassword(null)}
          onSubmit={handlePasswordUpdate}
        />
      ) : null}
      {userToDelete ? (
        <ConfirmDialog
          message={`Are you sure you want to permanently delete synced user "${userToDelete.email}"? This action cannot be undone.`}
          onYes={() => {
            void handleDeleteUser(userToDelete);
            setUserToDelete(null);
          }}
          onNo={() => setUserToDelete(null)}
          onCancel={() => setUserToDelete(null)}
        />
      ) : null}
    </div>
  );
};

export default UsersDirectoryPanel;
